/**
 * useNodeTokenSources Hook
 *
 * Single source of truth for the dynamic-content token set that the node
 * editor surfaces (token picker rail, field-fork popover, and inline
 * `${…}` autocomplete). Encapsulates the upstream-manifest + token-source +
 * run-data-preview wiring that ExpandedView, FieldForkContainer, and the
 * compact ParameterList all need so the three stay in lockstep.
 *
 * PERF: this runs eagerly when a node editor opens so inline `${…}` tokens are
 * ready before the user types. Upstream manifest/swagger fetches are shared via
 * react-query, so repeated opens hit cache rather than refetching.
 */

import { useMemo } from 'react';
import { useTokenSources, isConnectedToTrigger } from '@/hooks/useTokenSources';
import { useUpstreamManifests } from '@/hooks/useUpstreamManifests';
import { useAppLevelParameters } from '@/hooks/useAppLevelParameters';
import { usePreviewOutputs } from '@/hooks/useNodePreviewData';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNodeEditorStore, type SelectedNode } from '@/stores/nodeEditorStore';
import { resolveAgentParameterSchema } from '@/utils/agentParameterSchema';
import type { TokenSource } from '@/types/tokenSource';

export interface NodeTokenSourcesResult {
  /** Grouped dynamic-content tokens (trigger, actions, parameters, variables). */
  tokenSources: TokenSource[];
  /**
   * Run-data preview outputs merged with variables under the special
   * `__variables__` key — the shape TokenizedInput reads for type inference.
   */
  combinedOutputs: Record<string, unknown>;
  /** True once a successful run's outputs are available for previews. */
  hasRunData: boolean;
  /** True when preview data may be out of date relative to the definition. */
  isStale: boolean;
  /** Raw run-data preview outputs (without the merged `__variables__` key). */
  previewOutputs: Record<string, unknown>;
  /** Raw variable values from the last run. */
  variableOutputs: Record<string, unknown>;
  /** True while run-data preview outputs are loading. */
  isLoadingRunData: boolean;
  /** True while token sources are being computed. */
  tokensLoading: boolean;
  /** True while upstream manifests/swaggers are loading. */
  upstreamLoading: boolean;
}

/**
 * Compute the dynamic-content token set (and run-data previews) for the
 * currently-edited node.
 */
export function useNodeTokenSources(
  node: SelectedNode | null | undefined,
): NodeTokenSourcesResult {
  const draftAgentParameterSchema = useNodeEditorStore(
    (state) => state.parameterValues?.['_agentParameterSchema'],
  ) as Record<string, unknown> | null | undefined;
  const actions = useWorkflowStore(
    (state) => state.workflow?.definition?.actions ?? {},
  );

  const includeTrigger = useMemo(
    () => isConnectedToTrigger(node?.id, actions, node?.parentAgentName),
    [node?.id, actions, node?.parentAgentName],
  );

  const {
    manifests: upstreamManifests,
    swaggers: upstreamSwaggers,
    isLoading: upstreamLoading,
  } = useUpstreamManifests(node?.id);

  // Prefer the live Tool-tab draft so edits flow into tokens without a panel reopen.
  const agentParameterSchema = resolveAgentParameterSchema(
    draftAgentParameterSchema,
    node?.toolData?.agentParameterSchema as Record<string, unknown> | null | undefined,
  );

  const appLevelParameters = useAppLevelParameters();

  const { sources: tokenSources, isLoading: tokensLoading } = useTokenSources({
    currentNodeId: node?.id,
    parentAgentName: node?.parentAgentName,
    scopeContext: node?.scopeContext,
    includeTrigger,
    includeParameters: true,
    includeVariables: true,
    includeAgentParameters: !!node?.parentAgentName,
    manifests: upstreamManifests,
    swaggers: upstreamSwaggers,
    agentParameterSchema,
    ...(appLevelParameters ? { appLevelParameters } : {}),
  });

  const {
    outputs: previewOutputs,
    variables: variableOutputs,
    hasRunData,
    isLoading: isLoadingRunData,
    isStale,
  } = usePreviewOutputs(node?.id ?? null);

  const combinedOutputs = useMemo(
    () => ({
      ...previewOutputs,
      __variables__: variableOutputs,
    }),
    [previewOutputs, variableOutputs],
  );

  return {
    tokenSources,
    combinedOutputs,
    hasRunData,
    isStale,
    previewOutputs,
    variableOutputs,
    isLoadingRunData,
    tokensLoading,
    upstreamLoading,
  };
}
